import {useState, useCallback, useEffect, useRef} from 'react';

interface UseExpandableSectionOptions {
  /** Controlled expanded state. When provided, internal state is bypassed. */
  expanded?: boolean;
  /** Callback fired when the toggle is clicked (required for controlled mode). */
  onExpandedChange?: (expanded: boolean) => void;
  /** Initial expanded state for uncontrolled mode. Defaults to `false`. */
  defaultExpanded?: boolean;
}

interface UseExpandableSectionReturn {
  /** Whether the section is currently expanded. */
  isExpanded: boolean;
  /** Toggle handler to wire to the expand/collapse button. */
  handleExpandToggle: () => void;
}

/**
 * Manages expand/collapse state with support for both controlled and uncontrolled modes.
 *
 * **Uncontrolled** (default): pass nothing or just `defaultExpanded`.
 * **Controlled**: pass `expanded` and `onExpandedChange`.
 *
 * Providing `expanded` without `onExpandedChange` will log a development warning,
 * because the section will behave like a read-only controlled component.
 *
 * @example
 * // Uncontrolled
 * const { isExpanded, handleExpandToggle } = useExpandableSection();
 *
 * // Controlled
 * const [open, setOpen] = useState(false);
 * const { isExpanded, handleExpandToggle } = useExpandableSection({ expanded: open, onExpandedChange: setOpen });
 */
export const useExpandableSection = ({
  expanded: controlledExpanded,
  onExpandedChange,
  defaultExpanded = false,
}: UseExpandableSectionOptions = {}): UseExpandableSectionReturn => {
  const hasWarnedAboutMissingControlledCallback = useRef(false);
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      controlledExpanded !== undefined &&
      onExpandedChange === undefined &&
      !hasWarnedAboutMissingControlledCallback.current
    ) {
      hasWarnedAboutMissingControlledCallback.current = true;
      console.warn(
        '[useExpandableSection] `expanded` was provided without `onExpandedChange`. ' +
        'This puts the hook in controlled mode, so the section will appear read-only. ' +
        'Either provide `onExpandedChange` or omit `expanded` to use uncontrolled mode.'
      );
    }
  }, [controlledExpanded, onExpandedChange]);

  const handleExpandToggle = useCallback(() => {
    const next = !isExpanded;
    if (onExpandedChange) {
      onExpandedChange(next);
    } else {
      setInternalExpanded(next);
    }
  }, [isExpanded, onExpandedChange]);

  return {isExpanded, handleExpandToggle};
};

